import React, { useEffect, useMemo, useState } from "react";
import { saveMultipleHallSchedules, getAllHallSchedules } from "../config/firebaseService";

// Simple seed page to distribute 20 movies across 5 halls with showtimes and persist to Firestore
export default function SeedShowtimes() {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [movies, setMovies] = useState([]);
  const [loadMsg, setLoadMsg] = useState("");
  const [autoMsg, setAutoMsg] = useState("");
  const [autoSeeding, setAutoSeeding] = useState(false);
  // Ensure: for each hall, per day, per showtime, only one movie entry exists
  const enforceUniquePerHallShowtime = (byHall) => {
    const out = {};
    for (const hall of Object.keys(byHall || {})) {
      const seen = new Set();
      const filtered = [];
      for (const s of Array.isArray(byHall[hall]) ? byHall[hall] : []) {
        const date = String(s?.date || "").trim();
        const time = String(s?.time || "").trim();
        if (!date || !time) continue; // skip malformed entries
        const key = `${date}|${time}`;
        if (!seen.has(key)) {
          seen.add(key);
          filtered.push(s);
        }
        // If duplicate for same hall/date/time appears, ignore extras
      }
      // Keep times sorted for readability
      filtered.sort((a, b) => String(a.time).localeCompare(String(b.time)));
      out[hall] = filtered;
    }
    return out;
  };
  const halls = useMemo(() => [
    "Hall1",
    "Hall2",
    "Hall3",
    "Hall4",
    "Hall5",
    "Hall6",
    "Hall7",
    "Hall8",
  ], []);

  // Load 20 now playing movies from the local server (proxied TMDB ids/titles)
  const fetchNowPlaying = async () => {
    try {
      setLoadingMovies(true);
      setLoadMsg("Loading now playing movies...");
      const resp = await fetch("/api/movies/now_playing?page=1");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Expect ~20 items
      const top20 = (Array.isArray(data) ? data : []).slice(0, 20).map(m => ({ id: m.id, title: m.title }));
      setMovies(top20);
      setLoadMsg(`Loaded ${top20.length} movies.`);
      return top20;
    } catch (e) {
      console.error(e);
      setLoadMsg(`Failed to load movies: ${e?.message || e}`);
      setMovies([]);
      return [];
    } finally {
      setLoadingMovies(false);
    }
  };

  useEffect(() => {
    // Auto-load on open for previewing data
    fetchNowPlaying();
  }, []);

  // 5 showtimes per hall (8 halls * 5 = 40 slots). Each of 20 movies gets 2 showtimes.
  const plannedTimes = useMemo(() => [
    "10:00",
    "12:30",
    "15:00",
    "17:30",
    "20:00",
  ], []); // HH:mm 24h format

  const scheduleByHall = useMemo(() => {
    const dateISO = new Date().toISOString().slice(0, 10);
    const byHall = Object.fromEntries(halls.map((h) => [h, []]));

    const hallCount = halls.length; // 8
    const slotCount = plannedTimes.length; // 5
    const requiredMovies = 20;
    const picked = (movies || []).slice(0, requiredMovies);
    if (picked.length !== requiredMovies) return byHall;

    // Deterministic mapping: for movie i
    // - First show at (hall = i % 8, slot = i % 5)
    // - Second show at (hall = (i + 3) % 8, slot = (i + 2) % 5)
    // Guarantees: different hall and different slot for the same movie;
    // across all movies, produces 40 unique (hall,slot) cells.
    for (let i = 0; i < requiredMovies; i++) {
      const m = picked[i];
      const s1 = i % slotCount;
      const h1 = i % hallCount;
      const s2 = (i + 2) % slotCount;
      const h2 = (i + 3) % hallCount;

      const t1 = plannedTimes[s1];
      const t2 = plannedTimes[s2];

      byHall[halls[h1]].push({
        movieId: m.id,
        title: m.title,
        date: dateISO,
        time: t1,
        showtime: `${dateISO} ${t1}`,
      });

      byHall[halls[h2]].push({
        movieId: m.id,
        title: m.title,
        date: dateISO,
        time: t2,
        showtime: `${dateISO} ${t2}`,
      });
    }

  // Enforce: at most one movie per hall/date/time
  return enforceUniquePerHallShowtime(byHall);
  }, [halls, movies, plannedTimes]);

  // Auto-seed daily if not yet present in Firestore
  useEffect(() => {
    const runAutoSeed = async () => {
      try {
        setAutoSeeding(true);
        setAutoMsg("Checking today's schedules in Firestore...");
        const today = new Date().toISOString().slice(0, 10);

        // Fetch all halls and see if any schedule already has today's date
        const hallsData = await getAllHallSchedules();
        const alreadySeeded = hallsData.some(h => Array.isArray(h.schedule) && h.schedule.some(s => String(s.date) === today));
        if (alreadySeeded) {
          setAutoMsg(`Already seeded for ${today}.`);
          return;
        }

        // Optional local guard to avoid repeated writes within the same browser/day
        const lastSeed = localStorage.getItem("lastSeedDate");
        if (lastSeed === today) {
          setAutoMsg(`Local guard: already seeded today (${today}).`);
          return;
        }

        setAutoMsg("Loading movies and generating today's schedule...");
        const top20 = await fetchNowPlaying();
        if (!top20 || top20.length < 20) {
          setAutoMsg("Skipping auto-seed: need at least 20 movies to schedule 40 slots.");
          return;
        }

        // Build schedule using the same mapping logic
        const dateISO = today;
        const byHall = Object.fromEntries(halls.map((h) => [h, []]));
        const hallCount = halls.length; // 8
        const slotCount = plannedTimes.length; // 5
        const requiredMovies = 20;
        for (let i = 0; i < requiredMovies; i++) {
          const m = top20[i];
          const s1 = i % slotCount;
          const h1 = i % hallCount;
          const s2 = (i + 2) % slotCount;
          const h2 = (i + 3) % hallCount;
          const t1 = plannedTimes[s1];
          const t2 = plannedTimes[s2];
          byHall[halls[h1]].push({ movieId: m.id, title: m.title, date: dateISO, time: t1, showtime: `${dateISO} ${t1}` });
          byHall[halls[h2]].push({ movieId: m.id, title: m.title, date: dateISO, time: t2, showtime: `${dateISO} ${t2}` });
        }
  const safeByHall = enforceUniquePerHallShowtime(byHall);
  await saveMultipleHallSchedules(safeByHall);
        localStorage.setItem("lastSeedDate", today);
        setAutoMsg(`Auto-seeded schedules for ${today}.`);
      } catch (err) {
        console.error("Auto-seed failed", err);
        setAutoMsg(`Auto-seed failed: ${err?.message || err}`);
      } finally {
        setAutoSeeding(false);
      }
    };

    runAutoSeed();
    // We intentionally run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveMultipleHallSchedules(scheduleByHall);
      setResult({ ok: true, message: "Schedules saved to Firestore in collection 'cinemaHall' for Hall1..Hall5." });
    } catch (err) {
      console.error(err);
      setResult({ ok: false, message: err?.message || "Failed to save schedules" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main style={{ padding: 24 }}>
      <header>
        <h2>Seed Showtimes</h2>
      </header>

      <p>Schedule 8 halls with 5 time slots each (40 total). Each of the first 20 Now Playing movies gets exactly 2 showtimes (no same hall, different times).</p>

      {autoMsg && (
        <p style={{ color: autoSeeding ? "#2196f3" : "#9ccc65" }}>{autoMsg}</p>
      )}

      {movies.length < 20 && (
        <p style={{ color: "#ff9800" }}>Warning: Loaded only {movies.length} movies; need at least 20 to seed a full day properly.</p>
      )}

      <section aria-label="Controls" style={{ marginBottom: 12 }}>
        <button onClick={fetchNowPlaying} disabled={loadingMovies}>
          {loadingMovies ? "Loading movies..." : "Reload Now Playing"}
        </button>
        {loadMsg && (
          <p style={{ display: "inline", marginLeft: 10, color: "#666" }}>{loadMsg}</p>
        )}
        {loadingMovies && <progress aria-label="Loading movies" />}
      </section>

      <details>
        <summary>Preview schedule JSON</summary>
        <pre style={{ background: "#111", color: "#eee", padding: 12, borderRadius: 8, maxWidth: 640, overflowX: "auto" }}>
          <code>{JSON.stringify(scheduleByHall, null, 2)}</code>
        </pre>
      </details>

      <section aria-label="Save">
        <button onClick={handleSave} disabled={saving || movies.length === 0}>
          {saving ? "Saving..." : "Save schedules to Firestore"}
        </button>
        {saving && <progress aria-label="Saving schedules" />}
      </section>

      {result && (
        <p style={{ marginTop: 12, color: result.ok ? "#09c372" : "#ff4d4f" }}>{result.message}</p>
      )}
    </main>
  );
}
